import {isArray, isObject} from "lodash/lang";
import { IMAGE_SIZE, PRODUCT_LIST_TYPE_MAPPER } from '../constants/commons'
import {
  DYNAMIC_UI_TYPE_LIST, UI_TYPE_GROUP
} from "../pages/dynamicUi/constants";
import {USER_STATE_ACTIVE} from "../pages/user/constants";
import {isBlogContentInvalid} from "../pages/blogs/editorJsConfig";
import { getFormattedSeoUrl } from './seoHelper'

const TOKEN_IDENTIFIER = `ti_${process.env.REACT_APP_ACTIVE_ENVIRONMENT}`;

export const getAuthToken = () => {
  const token = localStorage.getItem(TOKEN_IDENTIFIER);
  return token ? JSON.parse(token) : {};
};

const capitalizeFirstLetter = (word) => {
  if (word?.length)
    return word[0].toUpperCase() + word.substring(1).toLowerCase()
  return ''
}

export const isSuperAdmin = (user) => {
  const _user = user ?? getAuthToken()?.user;
  return !!_user?.super_admin;
};

export const isUserLoggedIn = () => {
  return !!localStorage.getItem(TOKEN_IDENTIFIER);
};

export const clearAuthToken = () => {
  localStorage.removeItem(TOKEN_IDENTIFIER);
};

export const hasPermissionToFeature = (user, feature, superAdminOnly = false) => {
  return user && user.state === USER_STATE_ACTIVE && (user.super_admin || (user.staff && !superAdminOnly) || user?.features?.some((f)=>f.frontend_key.toLowerCase() === feature?.toLowerCase()))
};

export const updateAuthToken = (info) => {
  localStorage.setItem(TOKEN_IDENTIFIER, JSON.stringify({...getAuthToken(), ...info}));
};

export const isEmailValid = (emailData) => {
  return RegExp(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/g).test(emailData.trim());
};

export const generateFullUrl = (segment) => {
  return `${process.env.REACT_APP_DOMAIN ?? "https://www.jeevee.com"}${segment}`;
}

export const getNonDefaultItems = (listMap) => {
  return listMap?.filter((item) => !item.is_default && (!item.sub_types || item.sub_types.some((i) => !i.is_default)));
};

export const getNonDefaultGroupedItems = () => {
  return DYNAMIC_UI_TYPE_LIST?.filter((item) =>
    !item.is_default && item.value !== UI_TYPE_GROUP && (!item.sub_types || item.sub_types.some((i) => !i.is_default)));
};

export const safeImage = (obj, size = IMAGE_SIZE["1024"], defaultImg = 'https://jeevee.s3.us-east-2.amazonaws.com/images/util/med.png') => {
  const image = obj && isArray(obj) && obj.length ? obj[0][size] : isObject(obj) ? obj[size] : defaultImg;

  return image ? image
    : defaultImg;
};

export const getDiscountedPrice = (price, discount) => {
  const convertedPrice = parseFloat(price);
  return (convertedPrice - convertedPrice * (parseFloat(discount) / 100));
};

export const getTaxAmount = (price, tax = 0) => {
  return parseFloatSafe(price) * (parseFloatSafe(tax) / 100);
};

export const getFinalProductAmount = (price, discount, tax = 0) => {

  const discountedPrice = getDiscountedPrice(price, discount);

  const taxAmount = getTaxAmount(discountedPrice, tax);

  return (discountedPrice + taxAmount).toFixed(2);
};

export const flashSaleSoldOut = (product) => {
  const amountLeft = parseFloatSafe(product?.flash_sale?.total_quantity) - parseFloatSafe(product?.flash_sale?.quantity_reserved);
  return product?.flash_sale && amountLeft < 1;
};

export const parseIntSafe = (number, defaultVal) => {
  const num = parseInt(number);
  return num ? num : defaultVal ? defaultVal : 0;
};

export const appendUtcTimeZone = (dateString) => {
  return !dateString?.endsWith("Z") ? `${dateString}Z` : dateString;
};

export const parseFloatSafe = (number) => {
  const num = parseFloat(number);
  return num ? num : 0.0;
};

export const toKebabCase = (str) =>
  str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    ?.map(x => x.toLowerCase()).join("-") ?? str;

export const calculateLcm = (num1, num2) => {
  return (num1 * num2) / calculateGreatestCommonFactor(num1, num2);
};

export const calculateGreatestCommonFactor = (num1, num2) => {
  const remainder = num1 % num2;
  return remainder === 0 ? num2 : calculateGreatestCommonFactor(num2, remainder);
};

export const getSafeDiscount = (discount) => {
  return discount < 0 || discount > 100 ? 0 : discount
}

export const getFullName = (user) => {
  return `${user.first_name} ${user?.middle_name ? user.middle_name : ""} ${user.last_name}`
}

export const setPaginatedItems = (state, action) => {
  const sel = action.payload.type ?? state.selectedTab;
  const currentPage = action.payload.page ?? state[sel].meta.page;

  if (action.payload.loading !== undefined) {


    if (!state[sel].pageData[currentPage]) {
      state[sel].pageData[currentPage] = {};
    }
    state[sel].pageData[currentPage].error = '';
    state[sel].pageData[currentPage].loading = action.payload.loading;
    state[sel].meta.page = currentPage;

  } else {
    if (!state[sel].pageData[currentPage]) {
      return;
    }
    state[sel].pageData[currentPage].error = action.payload.error;
    state[sel].pageData[currentPage].loading = false;
    if (!action.payload.error) {

      state[sel].pageData[currentPage].init = true;
      if (action.payload.totalPages !== undefined) {
        state[sel].meta.totalPages = action.payload.totalPages;
      }
      state[sel].meta.page = action.payload.page;
      state[sel].pageData[currentPage].data = [...action.payload.data];

    }
  }
}

export const blogContentValidityCheck = (blocks)=>{
  return blocks.reduce((acc, value) => {

    const invalidCheck = isBlogContentInvalid(value);
    if (invalidCheck) {
      acc.invalid = [...acc.invalid, {...value, message: invalidCheck}];
    } else {
      acc.valid = [...acc.valid, value];
    }

    return acc;
  }, {valid: [], invalid: []})
}

export const paginationNumbers = (currentPage, totalPage, neighbours= 2, wildCard= '...' ) =>{
  if (currentPage > totalPage) {
    return []
  }

  if (totalPage <= 3 + neighbours) {
    //adding wildcard is not required in this case

    return Array.from({length: totalPage}, (_, i) => i+1);
  }

  let _pages = [1], _pagesUpdated = [], _prevPage;

  for (let i = currentPage - neighbours; i <= currentPage + neighbours; i++) {
    //make sure first and last pages are not repeated
    if (i < totalPage && i > 1) {
      _pages.push(i);
    }
  }
  _pages.push(totalPage);

  _pages.forEach(( page) =>{
    if (_prevPage != null) {
      if (page - _prevPage === 2) {
        //if the gap is 2 instead of showing ... show the actual number
        //eg. [1, 2, 3, 4, 5, ..., 20] is better than [1, ..., 3, 4, 5, ..., 20]
        //where 4 is the selected page
        _pagesUpdated.push(_prevPage + 1);
      } else if (page - _prevPage !== 1) {
        _pagesUpdated.push(wildCard);
      }
    }
    _pagesUpdated.push(page);
    _prevPage = page;
  });

  return _pagesUpdated;
}

export const fixTo2Place = (number) =>
    parseFloat(number ? (typeof number === "string" ? parseFloat(number) : number).toFixed(2).replace(/[.,]00$/, "") : "0")


export const toFixed=(num, fixed)=> {
  const re = new RegExp('^-?\\d+(?:\.\\d{0,' + ((fixed ?? 2) || -1) + '})?');
  const nums = num.toString().match(re);
  return nums && nums.length ? nums[0] : num;
}

export const sortPagesData=(data)=> {
  const _allData = data.reduce((acc, curr) =>

      curr.deletable ? {
        ...acc,
        removable: {...acc.removable, [curr.id]: curr}
      } : {
        ...acc,
        non: curr.type === "HOMEPAGE" ? {[curr.id]: curr, ...acc.non} : {...acc.non, [curr.id]: curr}
      }

    , {removable: {}, non: {}});

  return {
    ..._allData.non,
    ..._allData.removable
  };
}


export const isFromOrigin = (url) => {
  const originUrl = new URL(process.env.REACT_APP_DOMAIN);
  const link = new URL(url)

  return originUrl.hostname === link.hostname
}

export function parseProductTypeFromUrl(inputUrl) {
  const url = new URL(inputUrl);

  const paths = url.pathname.split("/").filter(Boolean);
  const firstPath = (paths[0] || "").toLowerCase();
  const secondPath = (paths[1] || "").toLowerCase();

  let value = "";
  const params = {};

  const isSearch = firstPath === "products" && secondPath === "search";

  // Extract parameters
  url.searchParams.forEach((val, key) => {
    if (!(isSearch && key === "query")) {
      params[key] = val;
    }
  });

  // Case 1: search
  if (isSearch) {
    value = url.searchParams.get("query") || "";
  }

  // Case 2: categories / brands
  else if (secondPath === "categories" || secondPath === "brands") {
    const match = paths[2]?.match(/-(\d+)$/);
    value = match ? match[1] : "";
  }

  // Case 3: product campaigns
  else if (firstPath === "product-campaigns") {
    const match = paths[1]?.match(/-(\d+)$/);
    value = match ? match[1] : "";
  }


  return {
    type: PRODUCT_LIST_TYPE_MAPPER[firstPath] ?? PRODUCT_LIST_TYPE_MAPPER[secondPath],
    // secondPath: PRODUCT_LIST_TYPE_MAPPER[secondPath] ?? secondPath,
    value,
    params,
  }
}

const generateLinkForProductDetailPage = (product) =>
  `/products/${getFormattedSeoUrl(product)}`

export function generateAllLinks({ products = [] } = {}) {

  const links = []
  // const generateUrl = `${process.env.NEXT_WEB_URL}'/api/dynamic-links'`


  // let i=5000;
  let i
  try {
    for (i = 0; i < products.length; i++) {
      const web = `${
        process.env.REACT_APP_DOMAIN
      }${generateLinkForProductDetailPage(products[i])}`

      links.push({
        // dynamic: _data.link,
        label: products[i].label,
        id: products[i].id,
        web,
      })
    }
  } catch (e) {
    console.log('Crash:', e)
  }

  return links
}